import { useMemo } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Card, StatusDot, freshness, livenessState, type StatusDotState } from '@diwata/ironvale'
import { useUI } from '@/store/ui'
import { DiwaAPI } from '@/api/client'
import { DomainsAPI, type Domain } from '@/api/domains'
import { GraphAPI, type GraphNode } from '@/api/graph'
import { SignalsAPI, type Signal } from '@/api/signals'
import { CRM_DOMAIN } from '@/store/domainSelection'
import { useSelection } from '@/store/selection'
import { useGraphFocus } from '@/store/graphFocus'
import { DomainActivity } from '@/components/ActivityHeatmap'
import { DomainExplainer } from '@/components/DomainExplainer'
import { HelpHint, Legend } from '@/components/HelpHint'
import './domain-page.css'

// The domain as a PLACE: one page per region — what lives here, what feeds it,
// what it's been saying, and the rules it listens by. Every section is a slice
// of an existing full surface and links back to it. This is the composition
// shared/public domains will reuse.

/** Shape of GET /domains/{id}/overview (products/diwa/src/diwa/domains/overview.py). */
interface DomainOverview {
  slug: string
  category: string | null
  description: string
  vocabulary: { entity_types: string[]; relation_types: string[]; words: Record<string, string> }
  springs: { name: string; status?: string; last_run?: string }[]
  has_rules: boolean
  counts: { entities_by_type: Record<string, number>; entities_total: number; signals_total: number; signals_fresh: number }
}

/** The slice of /matcher/schemas this page reads (full shape lives in Pipelines.tsx). */
interface RuleSchemaSlice {
  domain: string
  label: string
  description: string
  fields: { key: string; label: string }[]
}

/** Spring health → the shared liveness vocabulary (same states DomainPanel glyphs encode). */
const SPRING_STATE: Record<string, StatusDotState> = {
  ok: 'live',
  late: 'idle',
  rate_limited: 'idle',
  failing: 'error',
}

/** A domain "breathes" on a day cadence: touched within a day → live, a week → idle. */
const DOMAIN_PULSE = { live: 24 * 60 * 60 * 1000, idle: 7 * 24 * 60 * 60 * 1000 }

function lastTouched(nodes: GraphNode[]): number | null {
  let latest: number | null = null
  for (const n of nodes) {
    const t = Math.max(
      n.updated_at ? Date.parse(n.updated_at) || 0 : 0,
      n.created_at ? Date.parse(n.created_at) || 0 : 0,
    )
    if (t > 0 && (latest == null || t > latest)) latest = t
  }
  return latest
}

function SectionHead({ title, to, linkLabel, hint }: { title: string; to?: string; linkLabel?: string; hint?: string }) {
  return (
    <div className="dw-domain-section-head">
      <div className="dw-rail-section-title" style={{ marginBottom: 0 }}>
        {title}
        {hint && <HelpHint term={hint} />}
      </div>
      {to && linkLabel && (
        <Link className="dw-domain-more" to={to}>
          {linkLabel} →
        </Link>
      )}
    </div>
  )
}

export function DomainPage() {
  const { slug = '' } = useParams()
  const { diwaUrl } = useUI()
  const navigate = useNavigate()
  const select = useSelection((s) => s.select)
  const setFocus = useGraphFocus((s) => s.setFocus)
  const plain = useMemo(() => new DiwaAPI(diwaUrl), [diwaUrl])

  const { data: domainsData, isPending: domainsPending } = useQuery({
    queryKey: ['domains'],
    queryFn: () => new DomainsAPI(diwaUrl).list(),
  })
  // The built-in CRM domain is code-defined (no DB row), so it never appears in /domains —
  // fall back to the client-side object. It has no UUID, so UUID-keyed endpoints skip it.
  const listed: Domain | undefined = (domainsData?.domains ?? []).find((d) => d.slug === slug)
  const domain: Domain | undefined = listed ?? (slug === CRM_DOMAIN.slug ? CRM_DOMAIN : undefined)
  const hasRow = !!listed // only server-listed domains can hit /domains/{uuid}/*

  const { data: overview } = useQuery({
    queryKey: ['domain-overview', domain?.id],
    queryFn: () => plain.get<DomainOverview>(`/domains/${domain!.id}/overview`),
    enabled: hasRow,
  })
  const { data: network } = useQuery({
    queryKey: ['graph-network', slug],
    queryFn: () => new GraphAPI(diwaUrl).network(slug),
    enabled: !!domain,
  })
  const { data: allSignals } = useQuery({
    queryKey: ['signals'],
    queryFn: () => new SignalsAPI(diwaUrl).list(),
    enabled: !!domain,
  })
  const { data: schemasData } = useQuery({
    queryKey: ['matcher-schemas'],
    queryFn: () => plain.get<{ schemas: RuleSchemaSlice[] }>('/matcher/schemas'),
    enabled: !!domain,
  })

  if (!domain) {
    if (domainsPending) return <div className="dw-empty">Finding the domain…</div>
    return (
      <div className="dw-empty">
        No domain called “{slug}” in your verse.{' '}
        <Link className="dw-link" to="/domains">
          Back to your domains →
        </Link>
      </div>
    )
  }

  const nodes = (network?.nodes ?? []).filter((n) => !n.self)
  const pulse = livenessState(lastTouched(nodes), Date.now(), DOMAIN_PULSE)
  const entityTotal = overview?.counts.entities_total ?? nodes.length
  const entityTypes = overview?.vocabulary.entity_types ?? domain.entity_types
  const words = overview?.vocabulary.words ?? {}
  const byType = overview?.counts.entities_by_type ?? {}
  const springs = overview?.springs ?? []
  const signals = (allSignals ?? [])
    .filter((s) => s.domain === slug)
    .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
  const recent = [...nodes]
    .sort((a, b) => (Date.parse(b.created_at ?? '') || 0) - (Date.parse(a.created_at ?? '') || 0))
    .slice(0, 8)
  const schema = (schemasData?.schemas ?? []).find((s) => s.domain === slug)

  /** Recent-arrival click: hand the star to the constellation's inspect rail + center it. */
  function openInConstellation(n: GraphNode) {
    select({ id: n.id, name: n.name, type: n.type, world: n.world, score: n.score, domain: n.domain, attributes: n.attributes })
    setFocus(n.id)
    navigate('/graph')
  }

  return (
    <div className="dw-domain-page">
      <div className="dw-page-header">
        <div>
          <div className="dw-page-title">
            <StatusDot state={pulse} label={`activity: ${pulse}`} />
            {domain.name}
            {overview?.category && <span className="dw-domain-category">{overview.category}</span>}
            {domain.type === 'system' && <span className="dw-domain-category">system</span>}
          </div>
          <div className="dw-page-subtitle">{domain.description || 'A domain of your verse.'}</div>
          <div className="dw-domain-meta">
            <span>
              {entityTotal} star{entityTotal === 1 ? '' : 's'}
            </span>
            {overview && (
              <span>
                {overview.counts.signals_total} signal{overview.counts.signals_total === 1 ? '' : 's'}
                {overview.counts.signals_fresh > 0 && ` · ${overview.counts.signals_fresh} fresh`}
              </span>
            )}
            <span>
              {domain.shared
                ? `shared by ${domain.owner ?? 'another keeper'} — you hold it as ${domain.role ?? 'viewer'}`
                : 'yours'}
            </span>
            {domain.status === 'archived' && <span>archived — springs paused</span>}
          </div>
        </div>
        <Link className="dw-domain-more" to="/domains">
          all domains →
        </Link>
      </div>

      <div className="dw-domain-grid">
        {/* WHAT LIVES HERE — the region's vocabulary and its newest stars. */}
        <Card className="dw-domain-card dw-domain-card--wide">
          <SectionHead title="What lives here" to="/graph" linkLabel="open the constellation" />
          <DomainExplainer variant="mark" />
          <div className="dw-domain-types">
            {entityTypes.map((t) => (
              <div key={t} className="dw-domain-type">
                <HelpHint gloss={words[t]}>
                  <strong>{t}</strong>
                </HelpHint>
                {byType[t] != null && <span className="dw-domain-count">{byType[t]}</span>}
                {words[t] && <span className="dw-inbox-detail"> — {words[t]}</span>}
              </div>
            ))}
          </div>
          {recent.length > 0 ? (
            <>
              <div className="dw-domain-subhead">Recent arrivals</div>
              <div className="dw-domain-list">
                {recent.map((n) => (
                  <button key={n.id} type="button" className="dw-domain-row" onClick={() => openInConstellation(n)}>
                    <span className="dw-domain-row-glyph">✳</span>
                    <span className="dw-domain-row-title">{n.name}</span>
                    <span className="dw-inbox-detail">
                      {n.type}
                      {n.created_at && ` · ${freshness(n.created_at)}`}
                    </span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="dw-rail-empty">Nothing held here yet — stars arrive by spring or by your hand.</div>
          )}
        </Card>

        {/* ACTIVITY — this region's slice of the shared history heatmap. */}
        <Card className="dw-domain-card">
          <DomainActivity slug={slug} />
        </Card>

        {/* SPRINGS — what feeds this region, with liveness from lore health. */}
        <Card className="dw-domain-card">
          <SectionHead title="What feeds it" to="/pipelines" linkLabel="springs" hint="spring" />
          {springs.length > 0 && <Legend family="spring" className="dw-legend-springs" />}
          {springs.length === 0 ? (
            <div className="dw-rail-empty">
              {hasRow
                ? 'No springs feeding this domain — evidence arrives by your hand (Trove, capture).'
                : 'Springs are read per domain — this built-in domain fills by your hand.'}
            </div>
          ) : (
            <div className="dw-domain-list">
              {springs.map((sp) => (
                <div key={sp.name} className="dw-domain-row dw-domain-row--static">
                  <StatusDot state={SPRING_STATE[sp.status ?? ''] ?? 'neutral'} label={`${sp.name}: ${sp.status ?? 'unknown'}`} />
                  <span className="dw-domain-row-title">{sp.name}</span>
                  <span className="dw-inbox-detail">
                    {sp.status ?? 'no health yet'}
                    {sp.last_run && ` · last ${freshness(sp.last_run)}`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* SIGNALS — what this region has been saying lately. */}
        <Card className="dw-domain-card">
          <SectionHead title="Its signals" to="/signals" linkLabel="all signals" hint="signal" />
          {signals.length === 0 ? (
            <div className="dw-rail-empty">Quiet — routed signals land here as this domain's stars make news.</div>
          ) : (
            <div className="dw-domain-list">
              {signals.slice(0, 6).map((s: Signal) => (
                <Link key={s.id} className="dw-domain-row" to="/signals">
                  <span className="dw-domain-row-glyph">{s.status === 'fresh' ? '◆' : '◇'}</span>
                  <span className="dw-domain-row-title">{s.title}</span>
                  <span className="dw-inbox-detail">
                    {s.type} · {s.importance} · {freshness(s.created_at)}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </Card>

        {/* RULES — how the matcher listens for this region. */}
        <Card className="dw-domain-card">
          <SectionHead title="How it listens" to="/pipelines" linkLabel="edit rules" />
          {schema ? (
            <div className="dw-domain-rules">
              <div>
                <strong>{schema.label}</strong>
                <span className="dw-inbox-detail"> · {overview?.has_rules ? 'configured' : 'not configured — runs on defaults or stays silent'}</span>
              </div>
              <div className="dw-inbox-detail">{schema.description}</div>
              <div className="dw-inbox-detail">
                {schema.fields.length} setting{schema.fields.length === 1 ? '' : 's'}:{' '}
                {schema.fields.map((f) => f.label).join(' · ')}
              </div>
            </div>
          ) : (
            <div className="dw-rail-empty">No signal rules for this domain — the matcher is silent here.</div>
          )}
        </Card>
      </div>
    </div>
  )
}
